import get from 'lodash.get'
import {
  GAM_ADS_TAG_URL,
  GAM_SAMPLE_TAG_URL,
  GAM_SKIPPABLE_TAG_URL,
  GAM_SAMPLE_OVERLAY_TAG_URL
} from '../../constants/video'
import { scrubValues } from '../../helpers/obj'

import { Flooring } from '../../flooring/Flooring'
import VideoPrebidManager from './VideoPrebidManager'
import { formatQueryString, queryParamHas } from '../../helpers/getQueryParam'
import { IVideoAdTagOptions } from '../../../typings/IVideoOptions'
import { DFP_ID } from '../../constants'
import type Keywords from '../../keywords/Keywords'
import PageIdLookup from '../../pageIdLookup/PageIdLookup'
import { getS2SBeta } from '../s2sAdapter/BetaTest'
import { LiftTest } from '../../liftTest/LiftTest'
import { calculateUrTargeting } from '../../googletagFacade/helpers/targetingCalculators'
import type { AuctionHouse } from '../../auctionHouse/AuctionHouse'
import { IBidResponse } from '../../../typings/IPrebid'
import Logger from '../../helpers/Logger'
import type WebModel from '../../models/WebModel'
import StickyVideo from '../../stickyVideo/StickyVideo'
import AmazonAdapter from '../amazonAdapter/AmazonAdapter'
import GrowFaves from '../../growFaves/GrowFaves'
import { Video } from '../../slots/Video'
import SlotRepository from '../../slots/SlotRepository'

export const AUTOPLAY_CUSTOM = 3
export const AUTOPLAY_DESKTOP = 2
export const AUTOPLAY_MOBILE = 1
export const CATEGORY_SUBSTRING_LENGTH = 40
export const MOBILE_WIDTH_BREAKPOINT = 400
export const OVERLAY_LINE_ITEM_VERSION = 'overlay_2'

enum AdUnit {
  adhesion = 'adhesion_video',
  sticky = 'sticky_video',
  video = 'video',
  overlay = 'overlay'
}

export default class AdTags {
  private videoPrebidManager: VideoPrebidManager
  private prebid
  private pageId: number
  private inited = false
  private slot: Video
  static slotModel: ISlotModel = {
    adUnitId: 'video',
    selector: 'placeholder',
    type: 'Video'
  }

  constructor(
    private webModel: WebModel,
    private keywordService: Keywords,
    private liftTest: LiftTest,
    private auctionHouse: AuctionHouse
  ) {}

  private async init(options) {
    if (this.inited) {
      return
    }
    this.inited = true

    if (!window.pbjs) {
      const prebidjs = await import(
        /* webpackChunkName: "prebid" */ 'prebid.js'
      )
      this.prebid = prebidjs.default
    } else {
      this.prebid = window.pbjs
    }

    this.videoPrebidManager = new VideoPrebidManager(
      options,
      this.webModel,
      this.prebid,
      this.auctionHouse,
      this.liftTest
    )

    this.slot = new Video(AdTags.slotModel, this.webModel)
    SlotRepository.add(this.slot)
  }

  public async getAdTag(
    options: IVideoAdTagOptions,
    customParams: {} = {},
    skipPrebid: boolean = false
  ): Promise<string> {
    if (queryParamHas('test', 'skippable')) {
      return GAM_SKIPPABLE_TAG_URL
    }

    if (options.test) {
      return GAM_SAMPLE_TAG_URL
    }

    const tagParams = await this.getAdTagHelper(
      options,
      customParams,
      skipPrebid
    )
    return `${GAM_ADS_TAG_URL}?${formatQueryString(tagParams.params)}`
  }

  public async getAdTagAndHighBid(
    options: IVideoAdTagOptions,
    customParams: {} = {},
    skipPrebid: boolean = false
  ): Promise<{ adTagUrl: string; highBid?: IBidResponse }> {
    if (queryParamHas('test', 'skippable')) {
      return {
        adTagUrl: GAM_SKIPPABLE_TAG_URL
      }
    }

    if (options.test) {
      return {
        adTagUrl: GAM_SAMPLE_TAG_URL
      }
    }

    const helperResults = await this.getAdTagHelper(
      options,
      customParams,
      skipPrebid
    )
    return {
      adTagUrl: `${GAM_ADS_TAG_URL}?${formatQueryString(helperResults.params)}`,
      highBid: helperResults.highBid
    }
  }

  /**
   * This function will get the next highest video bid from the auctionHouse,
   * and return its adTagUrl + the bid itself.
   */
  public async getNextAdTagAndHighBid(
    options: IVideoAdTagOptions,
    customParams: {} = {},
    skipAuctionHouse: boolean = false
  ): Promise<{ adTagUrl: string; highBid?: IBidResponse }> {
    if (!this.inited) {
      await this.init(options)
    }
    this.pageId = await PageIdLookup.getPageId()

    options.adBreakType = options.adBreakType || 'linear'

    let highBid
    let prebidParams = {}

    if (!skipAuctionHouse) {
      const auctionResult =
        this.videoPrebidManager.getFormattedBidFromAuctionHouse({
          startDelay: options.startDelay,
          adBreakType: options.adBreakType
        })
      prebidParams = auctionResult.formattedBid
      highBid = auctionResult.highBid
      this.slot.highestPrebid = highBid
    }

    const addtlConsent = await this.getTcData()
    const params = this.createAdTagParams(
      options,
      prebidParams,
      customParams,
      addtlConsent
    )

    return {
      adTagUrl: `${GAM_ADS_TAG_URL}?${formatQueryString(params)}`,
      highBid
    }
  }

  public async getNonLinearAdTag(
    options: IVideoAdTagOptions,
    customParams: {} = {},
    skipPrebid: boolean = true
  ): Promise<string> {
    if (options.test) {
      return GAM_SAMPLE_OVERLAY_TAG_URL
    }

    const updatedCustomParams: {
      slot: string
      UR: string
      test?: string
    } = {
      ...customParams,
      slot: 'overlay',
      UR: '300'
    }

    if (queryParamHas('test', 'overlay')) {
      updatedCustomParams.test = 'overlay_test'
    }

    const { params, highBid } = await this.getAdTagHelper(
      options,
      updatedCustomParams,
      true
    )
    const updatedParams = {
      ...params,
      sz: '1x1',
      iu: encodeURIComponent(`/${DFP_ID}/${this.webModel.adunit}/overlay`),
      vad_type: 'nonlinear',
      vpos: 'midroll'
    }

    return `${GAM_ADS_TAG_URL}?${formatQueryString(updatedParams)}`
  }

  public async getNonLinearAdTagAndHighBid(
    options: IVideoAdTagOptions,
    customParams: {} = {},
    skipPrebid: boolean = true
  ): Promise<{ adTagUrl: string; highBid?: IBidResponse }> {
    if (options.test) {
      return { adTagUrl: GAM_SAMPLE_OVERLAY_TAG_URL }
    }

    this.pageId = await PageIdLookup.getPageId()

    let prebidParams = {}
    let highBid

    options.adBreakType = options.adBreakType || 'nonlinear'

    // need to fix options.unitType in video player? just override here?

    if (!skipPrebid && options.prebid) {
      await this.videoPrebidManager.runNonlinearVideoAuction({
        startDelay: options.startDelay
      })
      const auctionResult =
        this.videoPrebidManager.getFormattedBidFromAuctionHouse({
          startDelay: options.startDelay,
          adBreakType: options.adBreakType
        })
      prebidParams = auctionResult.formattedBid
      highBid = auctionResult.highBid
    }

    const addtlConsent = await this.getTcData()
    const params = this.createAdTagParams(
      options,
      prebidParams,
      customParams,
      addtlConsent
    )

    return {
      adTagUrl: `${GAM_ADS_TAG_URL}?${formatQueryString(params)}`,
      highBid
    }
  }

  private async getAdTagHelper(
    options: IVideoAdTagOptions,
    customParams: {} = {},
    skipPrebid: boolean = false
  ): Promise<{ params: {}; highBid?: IBidResponse }> {
    if (!this.inited) {
      await this.init(options)
    }
    this.pageId = await PageIdLookup.getPageId()

    options.adBreakType = options.adBreakType || 'linear'

    let prebidParams = {}
    let highBid
    if (!skipPrebid && options.prebid) {
      await this.videoPrebidManager.runVideoAuction({
        startDelay: options.startDelay
      })
      const auctionResult =
        this.videoPrebidManager.getFormattedBidFromAuctionHouse({
          startDelay: options.startDelay,
          adBreakType: options.adBreakType
        })
      prebidParams = auctionResult.formattedBid
      highBid = auctionResult.highBid
      this.slot.highestPrebid = highBid
    }

    const addtlConsent = await this.getTcData()
    const params = this.createAdTagParams(
      options,
      prebidParams,
      customParams,
      addtlConsent
    )
    return { params, highBid }
  }

  private createAdTagParams(
    options: IVideoAdTagOptions,
    prebidParams,
    customParams: {} = {},
    addtlConsent: string | null
  ) {
    const baseParams = this.getBaseParams(options)
    const amazonParams = AmazonAdapter.amazonTargeting[options.adunitId] || {}
    const mcmNetworkCode = this.webModel.mcmNetworkCode
    const slotName = options.adBreakType === 'nonlinear' ? 'overlay' : 'video'

    const adUnitPathMode = this.slot.getAdUnitPathMode()
    const adUnitPath =
      adUnitPathMode === 'mcm'
        ? `/${DFP_ID},${mcmNetworkCode}/${this.webModel.adunit}/${slotName}`
        : `/${DFP_ID}/${this.webModel.adunit}/${slotName}`

    const params: any = {
      correlator: Math.round(Date.now() / 1000),
      cust_params: encodeURIComponent(
        formatQueryString({
          ...baseParams,
          ...prebidParams,
          ...customParams,
          amznbid: amazonParams.amznbid,
          amzniid: amazonParams.amzniid,
          amznp: amazonParams.amznp,
          pid: this.pageId.toString(),
          xid: window.$mediavine.xid || '0',
          gid: GrowFaves.hasLoggedInUser ? '1' : '0',
          popout_placement: this.webModel.videoSettings.mobile_popout_placement,
          UR: this.calculateAndGetUR(prebidParams, options.adBreakType)
        })
      ),
      description_url: encodeURIComponent(options.permalink),
      env: 'vp',
      gdfp_req: '1',
      impl: 's',
      iu: encodeURIComponent(adUnitPath),
      npa: options.npa || 0,
      output: 'xml_vast3',
      sz: options.adBreakType === 'nonlinear' ? '1x1' : '640x480',
      unviewed_position_start: '1',
      url: encodeURIComponent(window.location.href),
      vad_type: options.adBreakType || 'linear',
      vpos: options.startDelay ? 'midroll' : 'preroll',
      addtl_consent: addtlConsent
    }

    // if window.__tcfapi is present, we're dealing with TCF2 so remove 'npa' param
    if (window.__tcfapi) {
      delete params.npa
    }

    // remove Google's Additional Consent string prop if not present
    if (!addtlConsent) {
      delete params.addtl_consent
    }

    return params
  }

  private getBaseParams(options: IVideoAdTagOptions): {} {
    let baseParamObject: {} = {
      google: this.webModel.google ? '1' : null,
      player_group: StickyVideo.group.name,
      player_version: StickyVideo.group.version,
      slot: this.getAdUnitType(options),
      test: options.test,
      up_next: options.videosPlayed || (options.playlist.length ? 0 : null), // TODO: clean up once site/video decoupling is complete
      video_id: options.slug,
      video_num: options.videoNumber,
      video_service: 3,
      hb_liv:
        options.adBreakType === 'nonlinear'
          ? OVERLAY_LINE_ITEM_VERSION
          : this.webModel.vidoLineItemVersion,
      wrapper_group: get(window, '$mediavine.web.model.versionGroup.name'),
      s2sVersion: getS2SBeta().name,
      wswy: this.webModel.wswy,
      ...this.getConditionalBaseParams(options),
      ...this.webModel.customTargeting,
      ...this.keywordService.getResults()
    }

    if (!options.omitFloorParams) {
      const bucket = `${Math.floor(Math.random() * 100) + 1}`
      const date = new Date()
      const partnerLift = this.liftTest.getPartnerLiftValue()?.join(',')

      baseParamObject = {
        ...baseParamObject,
        partnerLift,
        bucket,
        day: date.getUTCDate().toString(),
        dow: date.getUTCDay().toString(),
        hour: date.getUTCHours().toString(),
        month: String(date.getUTCMonth() + 1)
      }
    }

    return scrubValues(baseParamObject)
  }

  private getConditionalBaseParams(options: IVideoAdTagOptions): {} {
    const model = window.$mediavine.web.model
    const categories = model.categories.map((category) => category.slug)
    const optouts = model.optouts.map((optout) => optout.slug)
    const params: any = {}

    if (optouts.length) {
      params.optout = optouts.join(',')
    }

    if (categories.length) {
      params.categories = categories
        .map((category) => category.substring(0, CATEGORY_SUBSTRING_LENGTH))
        .join(',')
    }

    if (options.autoplay) {
      // Autoplay different DFP based on player width (account for non-approved units)
      if (options.unitType === AdUnit.video) {
        params.ap =
          options.contentWidth >= MOBILE_WIDTH_BREAKPOINT
            ? AUTOPLAY_DESKTOP
            : AUTOPLAY_MOBILE
      } else {
        params.ap = AUTOPLAY_CUSTOM
      }
      params.ctp = '0'
    } else {
      // Click to Play
      params.ap = '0'
      params.ctp = '1'
    }

    return params
  }

  /**
   * Returns whichever is greater, Prebid's hb_pb value or the floor value
   */
  private calculateAndGetUR(
    prebidParams: any,
    adBreakType: 'linear' | 'nonlinear' = 'linear'
  ): string {
    const topPrebidBid = prebidParams.hb_pb ? parseFloat(prebidParams.hb_pb) : 0

    const bidFloor =
      adBreakType === 'nonlinear'
        ? Flooring.getOverlayFloor(this.webModel)
        : Flooring.getVideoFloor(this.webModel)

    return calculateUrTargeting(Math.max(topPrebidBid, bidFloor))
  }

  private getAdUnitType(options: IVideoAdTagOptions): string {
    // This is a hack to convince google this is a good adUnit with history
    if (options.adBreakType === 'nonlinear') {
      return AdUnit.overlay
    } else if (options.autoplay && options.mobile) {
      return AdUnit.adhesion
    } else if (options.autoplay && !options.mobile) {
      return AdUnit.sticky
    }

    return options.unitType
  }

  /**
   * checks if tcfapi exists and returns null if not present
   * otherwise, it grabs the addtlConsent out of the tcModel
   */
  private async getTcData(): Promise<string | null> {
    try {
      if (!window.__tcfapi) {
        return null
      }

      return await new Promise((resolve, reject) => {
        window.__tcfapi('getTCData', 2, (tcData: any, success: boolean) =>
          success ? resolve(tcData.addtlConsent) : reject()
        )
      })
    } catch (err) {
      Logger.custom(
        ['debug', 'gpdr'],
        'GDPR: error getting tcData from window.__tcfapi'
      )
      return null
    }
  }
}
